/* ============================================================
   Notre Chanson — store.js
   Single source of truth for the funnel. Holds the user's
   answers, the selected product + cart, price config, and a
   handful of copy helpers (relation labels, gender agreement).
   Persisted to localStorage so progress survives a refresh and
   flows from the composer into the recap + cart pages.

   Exposed as window.NM. No framework — plain module pattern.
   Structured so a future Shopify hook only needs to read
   NM.state and map it to line-item properties.
   ============================================================ */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'nm_funnel_v1';

  /* ---- Price configuration (single place to retune the offer) ---- */

  // Les pages vivent sous /pages/<handle> sur Shopify et en <nom>.html sur le
  // site statique. On déduit la forme du chemin courant : un seul code pour
  // les deux, et plus d'URL en .html servies par tolérance de Shopify (qui
  // créaient des doublons d'adresse pour une même page).
  function route(name) {
    return /\/pages\//.test(location.pathname) ? '/pages/' + name : name + '.html';
  }
  var PRICING = {
    currency: '€',
    basePrice: 39,      // discounted headline price
    listPrice: 78,      // struck-through "old" price (39 € = −50 %)
    discountPct: 50,    // promo badge
    delivery: { standard: 'Livraison standard', standardCost: 0, deliveryLabel: 'sous 4 jours ouvrés' },
    bumps: {
      express:  { id: 'express',  label: 'Livraison Express',  price: 9.90, old: null,  desc: 'Recevez votre chanson en 24 heures au lieu de 4 jours.' },
      coffret:  { id: 'coffret',  label: 'Le Coffret Souvenir', price: 19.90, old: 33, desc: 'Le cadeau qui reste, pour la vie.' },
      couplet:  { id: 'couplet',  label: 'Ajouter le 3e couplet', price: 12.90, old: 25, desc: 'Un 3ᵉ couplet (le pont) qui fait souvent pleurer d\'amour.' }
    },
    /* Pourboire — montants FIXES : une variante Shopify a un prix figé,
       on ne peut pas envoyer un montant libre via l'API Storefront.
       Les pourcentages ne sont donc qu'un repère visuel (base 39 €). */
    tips: {
      tip5:  { id: 'tip5',  pct: 5,  price: 1.95 },
      tip10: { id: 'tip10', pct: 10, price: 3.90 },
      tip15: { id: 'tip15', pct: 15, price: 5.85 }
    }
  };

  /* ---- Default answer shape ---- */
  function freshState() {
    return {
      currentStep: '1a',
      answers: {
        relation: null,     // femme / mari / copine / …
        prenom: '',
        pronunciation: '',
        genre: null,        // chanson-francaise / soul / …
        voice: null,        // feminine / masculine / none
        qualities: '',      // comma-joined labels
        souvenirs: '',
        message: ''
      },
      email: '',
      product: {
        id: 'chanson-personnalisee',
        title: 'Chanson personnalisée',
        qty: 1
      },
      bumps: { express: false, coffret: false, couplet: false },
      tip: null            // null | 'tip5' | 'tip10' | 'tip15'
    };
  }

  var state = freshState();

  /* ---- Persistence ---- */
  function load() {
    try {
      var raw = global.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      // Shallow-merge each known branch so new fields keep their defaults.
      state.currentStep = saved.currentStep || state.currentStep;
      Object.assign(state.answers, saved.answers || {});
      Object.assign(state.bumps, saved.bumps || {});
      if (saved.tip && PRICING.tips[saved.tip]) state.tip = saved.tip;
      if (saved.email) state.email = saved.email;
      if (saved.product) Object.assign(state.product, saved.product);
    } catch (e) { /* corrupt / private mode → ignore, keep defaults */ }
  }

  function save() {
    try { global.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
    catch (e) { /* quota / private mode → session-only, acceptable */ }
  }

  function reset() {
    // Mutate in place so any held references (NM.state) stay valid.
    var f = freshState();
    state.currentStep = f.currentStep;
    state.answers = f.answers;
    state.email = f.email;
    state.product = f.product;
    state.bumps = f.bumps;
    state.tip = f.tip;
    save();
  }

  /* ---- Convenience setters ---- */
  function setAnswer(key, value) { state.answers[key] = value; save(); }
  function setStep(step)         { state.currentStep = step; save(); }
  function setEmail(v)           { state.email = v; save(); }
  function setBump(id, on)       { if (id in state.bumps) { state.bumps[id] = !!on; save(); } }
  /* id = 'tip5' | 'tip10' | 'tip15' | null (aucun pourboire) */
  function setTip(id)            { state.tip = (id && PRICING.tips[id]) ? id : null; save(); }
  function tipAmount()           { return state.tip && PRICING.tips[state.tip] ? PRICING.tips[state.tip].price : 0; }

  /* ---- Copy helpers (relation → gendered labels) ---- */
  var RELATION_LABEL = {
    femme: 'votre femme', mari: 'votre mari',
    copine: 'votre copine', copain: 'votre copain',
    mere: 'votre mère', pere: 'votre père',
    enfants: 'votre enfant', fratrie: 'votre frère ou votre sœur',
    ami: 'votre ami(e)', moi: 'vous', autre: 'cette personne'
  };
  var GENDER = {
    femme: 'f', copine: 'f', mere: 'f',
    mari: 'm', copain: 'm', pere: 'm',
    fratrie: 'n', ami: 'n', enfants: 'n', moi: 'n', autre: 'n'
  };

  function gender()        { return GENDER[state.answers.relation] || 'n'; }
  function relationLabel() { return RELATION_LABEL[state.answers.relation] || 'cette personne'; }
  /* First name if given, else a graceful gendered fallback for titles. */
  function displayName() {
    var p = (state.answers.prenom || '').trim();
    if (p) return p;
    return relationLabel();
  }

  var GENRE_LABEL = {
    'chanson-francaise': 'Chanson française',
    'chanson-quebecoise': 'Chanson québécoise',
    'piano-voix-emouvant': 'Piano-voix émouvant',
    'pop-romantique': 'Pop romantique',
    'acoustique': 'Guitare-voix intime',
    'soul': 'Soul', 'jazz-vocal': 'Jazz vocal',
    'country': 'Country', 'rock': 'Rock',
    'rai-francais': 'Raï français', 'zouk': 'Zouk'
  };
  var VOICE_LABEL = { feminine: 'Voix féminine', masculine: 'Voix masculine', none: 'Pas de préférence' };
  function genreLabel() { return GENRE_LABEL[state.answers.genre] || '—'; }
  function voiceLabel() { return VOICE_LABEL[state.answers.voice] || 'Pas de préférence'; }

  /* ---- Delivery date (always in the future, never a stale hard-coded day) ---- */
  var MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                   'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  var STANDARD_DELIVERY_DAYS = 4;
  function deliveryDate(daysAhead) {
    var d = new Date();
    d.setDate(d.getDate() + (typeof daysAhead === 'number' ? daysAhead : STANDARD_DELIVERY_DAYS));
    return d;
  }
  /* "22 juillet" — bare day+month, callers add their own prefix. */
  function deliveryLabel(daysAhead) {
    var d = deliveryDate(daysAhead);
    return d.getDate() + ' ' + MONTHS_FR[d.getMonth()];
  }

  /* ---- Multi-devise (Shopify Markets) ----
     Montants convertis + devise injectes par le theme (currency-init.liquid)
     dans window.NM_MONEY. Sans NM_MONEY -> repli EUR. */
  var CURRENCY='EUR';
  var _fmt=null;
  function parseMoneyStr(x){
    if(x==null||x==='') return null;
    var t=String(x).replace(/[^\d.,]/g,'');
    if(t.indexOf(',')>-1 && t.indexOf('.')>-1){
      if(t.lastIndexOf(',')>t.lastIndexOf('.')) t=t.replace(/\./g,'').replace(',', '.');
      else t=t.replace(/,/g,'');
    } else if(t.indexOf(',')>-1){ t=t.replace(',', '.'); }
    var n=parseFloat(t); return isFinite(n)?n:null;
  }
  function applyMoney(){
    var M=global.NM_MONEY; if(!M||!M.amounts) return;
    CURRENCY=M.currency||'EUR'; var a=M.amounts,n;
    if(a.song){ n=parseMoneyStr(a.song.price); if(n!=null)PRICING.basePrice=n; n=parseMoneyStr(a.song.compareAt); if(n!=null)PRICING.listPrice=n; }
    if(a.express){ n=parseMoneyStr(a.express.price); if(n!=null)PRICING.bumps.express.price=n; }
    if(a.coffret){ n=parseMoneyStr(a.coffret.price); if(n!=null)PRICING.bumps.coffret.price=n; n=parseMoneyStr(a.coffret.compareAt); if(n!=null)PRICING.bumps.coffret.old=n; }
    if(a.couplet){ n=parseMoneyStr(a.couplet.price); if(n!=null)PRICING.bumps.couplet.price=n; n=parseMoneyStr(a.couplet.compareAt); if(n!=null)PRICING.bumps.couplet.old=n; }
    if(a.tips){ n=parseMoneyStr(a.tips['5']); if(n!=null)PRICING.tips.tip5.price=n; n=parseMoneyStr(a.tips['10']); if(n!=null)PRICING.tips.tip10.price=n; n=parseMoneyStr(a.tips['15']); if(n!=null)PRICING.tips.tip15.price=n; }
    if(PRICING.listPrice>PRICING.basePrice) PRICING.discountPct=Math.round((1-PRICING.basePrice/PRICING.listPrice)*100);
  }
  function currency(){ return CURRENCY; }

  /* ---- Cart maths ---- */
  function money(n) {
    try {
      if(!_fmt||_fmt._cur!==CURRENCY){ _fmt=new Intl.NumberFormat('fr-FR',{style:'currency',currency:CURRENCY,minimumFractionDigits:2}); _fmt._cur=CURRENCY; }
      return _fmt.format(Number(n)||0);
    } catch(e){ return (Number(n).toFixed(2).replace('.', ',')) + ' ' + (CURRENCY==='EUR'?'€':CURRENCY); }
  }
  function cartTotals() {
    var qty = state.product.qty || 1;
    var subtotal = PRICING.basePrice * qty;
    var bumpTotal = 0;
    Object.keys(state.bumps).forEach(function (id) {
      if (state.bumps[id] && PRICING.bumps[id]) bumpTotal += PRICING.bumps[id].price;
    });
    var tip = tipAmount();
    var total = subtotal + bumpTotal + tip;
    /* Prix barré cumulé : valeur "avant remise" de tout le panier
       (chanson au prix barré + options à leur prix d'origine + pourboire). */
    var listBumps = 0;
    Object.keys(state.bumps).forEach(function (id) {
      if (state.bumps[id] && PRICING.bumps[id]) {
        var b = PRICING.bumps[id];
        listBumps += (b.old != null ? b.old : b.price);
      }
    });
    var listTotal = PRICING.listPrice * qty + listBumps + tip;
    return {
      qty: qty,
      subtotal: subtotal,
      bumpTotal: bumpTotal,
      tip: tip,
      total: total,
      listTotal: listTotal,
      savings: listTotal - total
    };
  }

  global.NM = {
    route: route,
    STORAGE_KEY: STORAGE_KEY,
    PRICING: PRICING,
    state: state,               // note: replaced on reset(), re-read via NM.state
    get answers() { return state.answers; },
    get bumps()   { return state.bumps; },
    load: load, save: save, reset: reset,
    setAnswer: setAnswer, setStep: setStep, setEmail: setEmail, setBump: setBump,
    setTip: setTip, tipAmount: tipAmount,
    gender: gender, relationLabel: relationLabel, displayName: displayName,
    genreLabel: genreLabel, voiceLabel: voiceLabel,
    deliveryDate: deliveryDate, deliveryLabel: deliveryLabel,
    money: money, cartTotals: cartTotals, currency: currency, applyMoney: applyMoney,
    // expose raw refs (kept in sync because state object identity is stable
    // except across reset(), after which callers should re-read NM.state)
    _getState: function () { return state; }
  };

  applyMoney();
  load();
})(window);
